import { useEffect, useState } from 'react'
import { Send, X } from 'lucide-react'
import { Button, Modal, toast } from '@/components/ui'
import { ApiError } from '@/lib/api/jsonFetch'
import { useSendPreview, useSendQuote } from '../api/queries'

/**
 * Send Quote dialog (Sprint 12b).
 *
 * Mirrors Odoo's "Send by email" dialog — to / cc / bcc / subject /
 * body, all editable, with the canonical defaults pre-filled from
 * `/api/quotes/{id}/send-preview`. The PDF is automatically attached
 * server-side (no upload step here).
 *
 * Submit fires `useSendQuote` with the user-edited fields. On
 * success, the parent QuoteDetail's status will reflect the
 * approved → sent transition (cache invalidation handled by the
 * mutation).
 */

export interface SendQuoteDialogProps {
  open: boolean
  quoteId: number | null
  quoteNumber: string | null
  onClose: () => void
}

function parseAddresses(raw: string): string[] {
  return raw
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

export function SendQuoteDialog({
  open, quoteId, quoteNumber, onClose,
}: SendQuoteDialogProps) {
  const previewId = open && quoteId !== null ? quoteId : null
  const { data: preview, isLoading } = useSendPreview(previewId)
  const send = useSendQuote()

  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')

  // Pre-fill from the preview when it arrives. Subsequent user edits
  // win — we only seed once per (open, preview) pair.
  useEffect(() => {
    if (!open) return
    if (!preview) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-fills form on open + when preview arrives; gated by guards above.
    setTo(preview.recipient ?? '')
    setSubject(preview.subject ?? '')
    setBody(preview.body ?? '')
    // Don't clear cc/bcc on every reopen — let the dialog remember
    // additions if the user backed out and reopened.
  }, [open, preview])

  // Reset on close so a reopened dialog gets fresh defaults.
  useEffect(() => {
    if (open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot reset on dialog close.
    setTo('')
    setCc('')
    setBcc('')
    setSubject('')
    setBody('')
  }, [open])

  function handleSend() {
    if (quoteId === null) return
    if (!to.trim()) {
      toast.danger('Recipient (To) is required')
      return
    }
    send.mutate(
      {
        id: quoteId,
        override_recipient: to.trim(),
        cc: parseAddresses(cc),
        bcc: parseAddresses(bcc),
        subject: subject.trim() || undefined,
        body: body || undefined,
      },
      {
        onSuccess: (data) => {
          toast.success(`Quote sent to ${data.recipient}`)
          onClose()
        },
        onError: (err) => {
          toast.danger(
            err instanceof ApiError ? err.message : 'Send failed',
          )
        },
      },
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next && !send.isPending) onClose()
      }}
      tone="info"
      title={`Send ${quoteNumber ?? 'quote'} to customer`}
      description="Edit recipients and message before sending. The quote PDF is attached automatically."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={send.isPending}
          >
            <X size={14} /> Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            loading={send.isPending}
            disabled={!to.trim() || isLoading}
          >
            <Send size={14} /> Send
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div className="py-6 text-center text-sm text-tertiary">
          Loading defaults…
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">To</span>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="customer@company.com"
              className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm focus:border-border-focus focus:outline-none"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">
              CC <span className="text-tertiary text-xs">(comma-separated)</span>
            </span>
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="rep@spi.com, manager@spi.com"
              className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm focus:border-border-focus focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">
              BCC <span className="text-tertiary text-xs">(comma-separated)</span>
            </span>
            <input
              type="text"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              placeholder="archive@spi.com"
              className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm focus:border-border-focus focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm focus:border-border-focus focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary">Message</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={9}
              className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm font-sans focus:border-border-focus focus:outline-none"
            />
          </label>
          <p className="text-xs text-tertiary">
            Sender: <code>orders@siliconesplus.com</code> · Attachment:
            quote PDF (auto-rendered)
          </p>
        </div>
      )}
    </Modal>
  )
}
